import * as React from "react"
import { Container, Hidden, Row, Visible } from "react-grid-system"
import { HorizontalSpacer, Spacer } from "../../../atoms/spacers"
import SmallButton from "../../../atoms/buttons/SmallButton"
import { Link } from "react-router-dom"
import Card from "../../../atoms/Card"
import { ArrowBack } from "../../../atoms/icons/ArrowBack"
import CustomizeTitle from "../../../molecules/store/CustomizeAndOrder/CustomizeTitle"
import { SlideOutHeight } from "../../../atoms/animations/SlideOutHeight"
import CustomizeTerminals from "../../../organisms/store/CustomizeAndOrder/Terminals"
import CustomizeDataPlan from "../../../organisms/store/CustomizeAndOrder/DataPlan"
import { CartStructure, Product, Variant } from "../../../interfaces/SaleorTypes"
import {
  CustomizeAccessoriesFinalBar,
  CustomizeDataPlanFinalBar,
  CustomizeTerminalFinalBar,
} from "../../../molecules/store/CustomizeAndOrder/CustomizedFinalBars"
import H2 from "../../../atoms/typography/H2"
import H3 from "../../../atoms/typography/H3"
import SmallColorfulButton from "../../../atoms/buttons/SmallColorfulButton"
import { AddShoppingCartIcon } from "../../../atoms/icons/AddShoppingCartIcon"
import { theme } from "../../../theme"
import { CheckCircleIcon } from "../../../atoms/icons/CheckCircleIcon"
import { EditIcon } from "../../../atoms/icons/EditIcon"
import CustomizeAccessories from "../../../organisms/store/CustomizeAndOrder/Accessories"
import { CartSolutionOrder } from "../../../../utils/models"
import { v4 as uuidv4 } from "uuid"
import { getWarranty } from "../../../../hooks-saleor/getWarrantyQuery"
import { getFormattedPrice } from "../../../../utils/cart-helpers"

interface Props {
  addToCart: (products: CartStructure[]) => void
  removeFromCart: (products: CartStructure[]) => void
  addSolution: (solution: CartSolutionOrder) => void
  removeSolution: (solutionId: string) => void
  setCartOpen: any
  terminalType: "ku" | "secure"
}

const CustomizeAndOrderPage: React.FC<Props> = ({
  addToCart,
  removeFromCart,
  addSolution,
  removeSolution,
  setCartOpen,
  terminalType,
}) => {
  const { warranty } = getWarranty()
  const isKu = window.location.href.includes("ku")

  const [dataPlanOpen, setDataPlanOpen] = React.useState(false)
  const [accessoriesOpen, setAccessoriesOpen] = React.useState(false)

  const [showOrderOverview, setShowOrderOverview] = React.useState(false)

  React.useEffect(() => {
    document.title = "Customize order"
  }, [])

  //terminal:
  const [terminalsOpen, setTerminalsOpen] = React.useState(true)
  const [selectedTerminal, setSelectedTerminal] = React.useState<undefined | Product>(undefined)
  const [terminalAmount, setTerminalAMount] = React.useState(1)
  const [extendedWarranties, setExtendedWarranties] = React.useState(0)
  const [modem, setModem] = React.useState<undefined | Product>(undefined)
  const [terminalTotal, setTerminalTotal] = React.useState(0)
  const [hideDataPlan, setHideDataPlan] = React.useState(false)
  const [currency, setCurrency] = React.useState<undefined | string>()

  const onClickNextStepTerminals = (
    terminal: Product,
    terminalAmounts: number,
    extendedWarratyNumber: number,
    modemPicked: Product | undefined,
    terminalTotal: number,
    shouldHideDp?: boolean,
  ) => {
    setTerminalsOpen(false)
    setTerminalAMount(terminalAmounts)
    setExtendedWarranties(extendedWarratyNumber)
    setModem(modemPicked)
    setTerminalTotal(terminalTotal)
    setSelectedTerminal(terminal)
    setCurrency(terminal.variants[0].pricing.price.currency)

    if (shouldHideDp === true) {
      setAccessoriesOpen(true)
      setHideDataPlan(true)
    } else {
      setHideDataPlan(false)
      setDataPlanOpen(true)
    }
  }

  //data plan
  const [dpVariant, setDpVariant] = React.useState<undefined | Variant>(undefined)
  const [dpTotal, setDpTotal] = React.useState<undefined | number>(undefined)

  const onClickNextStepDataPlans = (dataplanTotal: number, dp?: Variant) => {
    setDpVariant(dp)
    setDpTotal(dataplanTotal)

    setTerminalsOpen(false)
    setDataPlanOpen(false)
    setAccessoriesOpen(true)
  }

  //accessories
  const [selectedAccessories, setSelectedAccessories] = React.useState<Array<{ product: Product; amount: number }>>([])
  const [accessoryTotal, setAccessoryTotal] = React.useState<undefined | number>(undefined)

  const onClickOrderOverview = (accessoryOrder: Array<{ product: Product; amount: number }>, total: number) => {
    setTerminalsOpen(false)
    setDataPlanOpen(false)
    setAccessoriesOpen(false)
    setSelectedAccessories(accessoryOrder)
    setAccessoryTotal(total)

    setShowOrderOverview(true)
    window.scrollTo(0, 0)
  }

  const [uuid, setUuid] = React.useState(uuidv4())

  //add to cart
  const [addedToCart, setAddedToCart] = React.useState(false)
  const onClickAddToCart = () => {
    let productsToAdd: Array<CartStructure> = []
    const startDate = new Date()
    if (dpVariant !== undefined && dpVariant.pricing.price !== undefined) {
      const dpProduct: Product = {
        id: dpVariant.product!.id,
        name: dpVariant.product!.name,
        metadata: [...dpVariant.product!.metadata, { key: "dataPlanStartDate", value: startDate.toISOString() }],
        media: dpVariant.product!.media,
        description: dpVariant.product!.description,
        variants: [dpVariant],
        category: dpVariant.product!.category,
      }
      const dp: CartStructure = { product: dpProduct, amount: terminalAmount }
      productsToAdd = [...productsToAdd, dp]
    }

    if (extendedWarranties > 0) {
      const w: CartStructure = { product: warranty, amount: terminalAmount }
      productsToAdd = [...productsToAdd, w]
    }

    const t: CartStructure = { product: selectedTerminal!, amount: terminalAmount }
    const acs: Array<CartStructure> = selectedAccessories

    productsToAdd = [...productsToAdd, t]
    productsToAdd = [...productsToAdd, ...acs]

    const solution: CartSolutionOrder = {
      id: uuid,
      terminal: selectedTerminal!,
      terminalAmount: terminalAmount,
      dataPlan: dpVariant,
      modem: modem,
      extendedWarranty: extendedWarranties,
    }

    addSolution(solution)
    addToCart(productsToAdd)
    setAddedToCart(true)

    setCartOpen(true)
  }

  const onClickEdit = () => {
    let productsToAdd: Array<CartStructure> = []
    const startDate = new Date()
    if (dpVariant !== undefined && dpVariant.pricing.price !== undefined) {
      const dpProduct: Product = {
        id: dpVariant.product!.id,
        name: dpVariant.product!.name,
        metadata: [...dpVariant.product!.metadata, { key: "dataPlanStartDate", value: startDate.toISOString() }],
        media: dpVariant.product!.media,
        description: dpVariant.product!.description,
        variants: [dpVariant],
        category: dpVariant.product!.category,
      }
      const dp: CartStructure = { product: dpProduct, amount: terminalAmount }
      productsToAdd = [...productsToAdd, dp]
    }
    if (extendedWarranties > 0) {
      const w: CartStructure = { product: warranty, amount: terminalAmount }
      productsToAdd = [...productsToAdd, w]
    }
    const t: CartStructure = { product: selectedTerminal!, amount: terminalAmount }
    const acs: Array<CartStructure> = selectedAccessories

    productsToAdd = [...productsToAdd, t]
    productsToAdd = [...productsToAdd, ...acs]

    removeSolution(uuid)
    removeFromCart(productsToAdd)
    window.location.reload()
  }

  return (
    <div>
      <Container>
        <HorizontalSpacer spacing={2} />
        <Link to="/store">
          <SmallButton title="Back to store" leftIcon={<ArrowBack />} />
        </Link>
        <HorizontalSpacer spacing={2} />

        {showOrderOverview && addedToCart && (
          <div>
            <Row justify="center" align="center">
              <CheckCircleIcon />
              <Spacer spacing={0.5} />
              <H2>Your order was added to your cart!</H2>
              <Spacer spacing={2} />
              <SmallButton title="Edit" icon={<EditIcon />} onClick={onClickEdit} />
            </Row>
            <HorizontalSpacer spacing={2} />
          </div>
        )}

        <>
          <Card>
            {selectedTerminal ? (
              <CustomizeTerminalFinalBar
                amount={terminalAmount}
                name={selectedTerminal.name}
                modemName={modem?.name ?? ""}
                warranty={extendedWarranties}
                total={terminalTotal}
                currency={currency}
                open={terminalsOpen}
                onClick={() => setTerminalsOpen(!terminalsOpen)}
                disabled={addedToCart}
              />
            ) : (
              <CustomizeTitle
                title="Terminals"
                step={1}
                open={terminalsOpen}
                onClick={() => setTerminalsOpen(!terminalsOpen)}
              />
            )}
            <SlideOutHeight isVisible={terminalsOpen}>
              <HorizontalSpacer spacing={1} />
              <CustomizeTerminals
                terminalType={terminalType}
                onClickNextStep={onClickNextStepTerminals}
                defaultValues={{
                  selectedModem: modem,
                  numberOfWarranties: extendedWarranties,
                  terminalAmount: terminalAmount,
                }}
              />
            </SlideOutHeight>
          </Card>

          <HorizontalSpacer spacing={2} />
          {hideDataPlan === false && (
            <Card>
              {dpTotal ? (
                <CustomizeDataPlanFinalBar
                  total={dpTotal}
                  currency={currency}
                  onClick={() => setDataPlanOpen(!dataPlanOpen)}
                  open={dataPlanOpen}
                  dataPlanName={dpVariant?.metadata[0]?.value}
                  dataPlanSize={dpVariant?.name}
                  disabled={addedToCart}
                />
              ) : (
                <CustomizeTitle
                  title="Data plan"
                  step={2}
                  open={dataPlanOpen}
                  onClick={() => setDataPlanOpen(!dataPlanOpen)}
                  disabled={!selectedTerminal}
                />
              )}
              <SlideOutHeight isVisible={dataPlanOpen}>
                <HorizontalSpacer spacing={1} />
                <CustomizeDataPlan
                  onClickNextStep={onClickNextStepDataPlans}
                  numberOfTerminals={terminalAmount}
                  defaultValues={{ dataPlanVariant: dpVariant }}
                  terminalTotal={terminalTotal}
                  currency={currency}
                />
              </SlideOutHeight>
            </Card>
          )}
          <HorizontalSpacer spacing={2} />
          <Card>
            {accessoryTotal !== undefined ? (
              <CustomizeAccessoriesFinalBar
                onClick={() => setAccessoriesOpen(!accessoriesOpen)}
                open={accessoriesOpen}
                items={selectedAccessories}
                total={accessoryTotal}
                currency={currency}
                disabled={addedToCart}
              />
            ) : (
              <CustomizeTitle
                title="Accessories"
                step={3}
                open={accessoriesOpen}
                onClick={() => setAccessoriesOpen(!accessoriesOpen)}
                disabled={!selectedTerminal}
              />
            )}
            <SlideOutHeight isVisible={accessoriesOpen}>
              <HorizontalSpacer spacing={1} />
              <CustomizeAccessories
                onClickOrderOverview={onClickOrderOverview}
                defaultValues={{ accessories: selectedAccessories }}
                terminalTotal={terminalTotal}
                dpTotal={dpTotal ?? 0}
                currency={currency}
              />
            </SlideOutHeight>
          </Card>
        </>

        {showOrderOverview && !addedToCart && (
          <div>
            <HorizontalSpacer spacing={2} />
            <Row justify="around" nogutter align="center">
              <div>
                <H2>Total: {getFormattedPrice(terminalTotal + (dpTotal ?? 0) + (accessoryTotal ?? 0), currency)}</H2>
                <H3>+ shipping (calculated at checkout)</H3>
              </div>
              <Hidden xs>
                <SmallColorfulButton
                  title="Add to cart"
                  icon={<AddShoppingCartIcon color={theme.colors.white} />}
                  onClick={onClickAddToCart}
                  variant={isKu ? "blue" : "green"}
                />
              </Hidden>
            </Row>
            <Visible xs>
              <HorizontalSpacer spacing={2} />
              <Row justify="center">
                <SmallColorfulButton
                  title="Add to cart"
                  icon={<AddShoppingCartIcon color={theme.colors.white} />}
                  onClick={onClickAddToCart}
                  variant={isKu ? "blue" : "green"}
                />
              </Row>
            </Visible>
          </div>
        )}
        <HorizontalSpacer spacing={2} />
      </Container>
      <HorizontalSpacer spacing={4} />
    </div>
  )
}

export default CustomizeAndOrderPage
